import type { BootAuthResponse, MaintenanceModeRichText } from '@/lib/types/boot';

export const MAINTENANCE_PAGE_TEST_ID = 'maintenance-page';

/** Shown when the publisher enabled maintenance without writing any copy. */
export const MAINTENANCE_FALLBACK_MESSAGE_KEY = 'maintenance.underConstruction.description';

export const resolveMaintenanceModeEnabled = (authBoot?: BootAuthResponse | null): boolean =>
  Boolean(authBoot?.storeTheme?.storeSettings?.isMaintenanceModeEnabled);

/**
 * `storeSettings` is where the dashboard writes today; `general` is where
 * older themes kept it. Falls back per field, like store v1, so a colour set
 * on one and text on the other still combine.
 */
export const resolveMaintenanceModeRichText = (
  authBoot?: BootAuthResponse | null,
): MaintenanceModeRichText | undefined => {
  const fromSettings = authBoot?.storeTheme?.storeSettings?.maintenanceModeRichText;
  const fromGeneral = authBoot?.storeTheme?.general?.maintenanceModeRichText;
  if (!fromSettings) return fromGeneral;
  if (!fromGeneral) return fromSettings;
  return {
    text: fromSettings.text?.trim() ? fromSettings.text : fromGeneral.text,
    fontColor: fromSettings.fontColor ?? fromGeneral.fontColor,
  };
};

/** True when the sanitized rich text has visible characters, not just tags. */
export const hasMaintenanceMessageContent = (sanitizedHtml?: string): boolean =>
  Boolean(sanitizedHtml?.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim());

export interface MaintenanceModeSsePayload {
    isMaintenanceModeEnabled: boolean;
    maintenanceModeRichText?: {
        text?: string;
        fontColor?: string;
    };
}

export const mergeAuthBootMaintenanceMode = (
    authBoot: BootAuthResponse,
    payload: MaintenanceModeSsePayload,
): BootAuthResponse => {
    const storeSettings = {
        ...authBoot.storeTheme?.storeSettings,
        isMaintenanceModeEnabled: payload.isMaintenanceModeEnabled,
    };

    if (payload.maintenanceModeRichText) {
        const mergedRichText = mergeMaintenanceModeRichText(
            authBoot.storeTheme?.storeSettings?.maintenanceModeRichText,
            payload.maintenanceModeRichText,
        );
        if (mergedRichText) {
            storeSettings.maintenanceModeRichText = mergedRichText;
        }
    }

    return {
        ...authBoot,
        storeTheme: {
            ...authBoot.storeTheme,
            storeSettings,
        },
    };
};

function mergeMaintenanceModeRichText(
    existing?: MaintenanceModeRichText,
    patch?: MaintenanceModeSsePayload['maintenanceModeRichText'],
): MaintenanceModeRichText | undefined {
    if (!patch) {
        return existing;
    }

    const text = patch.text ?? existing?.text;
    if (text === undefined) {
        return existing;
    }

    return {
        fontColor: patch.fontColor ?? existing?.fontColor,
        text,
    };
}
